<script setup lang="ts">
import type { Product } from '~/types/product.type'

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
})

const { t } = useI18n({})

const productStore = useProductStore()
const fetchedProducts = await productStore.getProductList(props.category, props.type)
const categories = await productStore.getProductCategories()
const category = computed(() => categories.data.value?.find(x => x.slug === props.type))
const filteredProducts = ref<Product[]>(fetchedProducts.data.value ?? [])

const isDescriptionOpen = ref(true)
const hasDescription = computed(() => !!category.value?.description && category.value?.description !== '')
const isFilterOpen = ref(false)

const isLoading = computed(() => fetchedProducts.status.value === 'pending' || fetchedProducts.status.value === 'idle')
</script>

<template>
  <div class="flex w-full flex-1 flex-col gap-8 lg:flex-row">
    <Transition name="slide-in">
      <div v-show="isFilterOpen" class="lg:hidden flex flex-col fixed bg-white z-9999 h-screen w-screen left-0 top-0 lg:w-full lg:h-auto lg:relative px-4 lg:py-0 pb-4 pt-16 lg:py-0 overflow-auto">
        <button class="w-8 h-8 top-4 right-4 absolute lg:hidden" @click="isFilterOpen = false">
          <span class="i-mdi-close w-8 h-8 flex" />
        </button>
        <ProductListFilter v-model:filteredProducts="filteredProducts" :products="productStore.productList" />
      </div>
    </Transition>

    <Transition name="fade" mode="out-in">
      <template v-if="!isLoading">
        <div class="hidden lg:flex lg:flex-row flex-col fixed bg-white z-20 h-screen w-screen left-0 top-0 lg:w-72 flex-none lg:h-auto lg:relative lg:py-0 pb-4 pt-16 lg:py-0 overflow-auto">
          <button class="w-8 h-8 top-4 right-4 absolute lg:hidden" @click="isFilterOpen = false">
            <span class="i-mdi-close w-8 h-8 flex" />
          </button>
          <ProductListFilter v-model:filteredProducts="filteredProducts" :products="productStore.productList" />
        </div>
      </template>
      <div v-else class="hidden lg:flex lg:flex-row flex-col fixed bg-white z-20 h-screen w-screen left-0 top-0 lg:w-72 flex-none lg:h-auto lg:relative lg:py-0 pb-4 pt-16 lg:py-0 overflow-auto">
        <div
          class="bg-gray/20 w-full rounded shimmer min-h-[80vh] hidden lg:flex flex-col items-center justify-center animate-pulse
 "
        >
          <span class="i-mdi-loading w-8 h-8 animate-spin opacity-20" />
        </div>
      </div>
    </Transition>

    <Transition name="fade" mode="out-in">
      <div v-if="!isLoading">
        <PrimeButton class="lg:hidden flex items-center justify-center" @click="isFilterOpen = true">
          <div class="flex items-center justify-center gap-2">
            <span class="i-mi-filter w-5 h-5 flex items-center justify-center mt-1" />
            <p>
              {{ t('product.filter') }}
            </p>
          </div>
        </PrimeButton>

        <div>
          <Component :is="hasDescription ? 'button' : 'div'" class="w-full gap-4 justify-between text-left flex items-center mb-4" @click="isDescriptionOpen = !isDescriptionOpen">
            <h1 class="text-xl font-bold">
              {{ category?.title ?? t('navbar.products') }} <span class="text-base text-gray">({{ filteredProducts.length }})</span>
            </h1>
            <span
              v-if="hasDescription"
              class="i-mdi-chevron-down w-5 h-5 text-black duration-100" :class="{
                'transform rotate-180': isDescriptionOpen,
              }"
            />
          </Component>
          <div v-if="isDescriptionOpen" class="mb-4 flex flex-col gap-2" v-html="category?.description" />
          <div v-auto-animate class="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-4">
            <ProductItem
              v-for="product in filteredProducts"
              :key="product.id"
              :product="product"
              :show-link="false"
              class="w-full"
            />
          </div>
        </div>
      </div>
      <div v-else class="w-full">
        <div
          class="bg-gray/20 w-full rounded shimmer min-h-[80vh] hidden lg:flex flex-col items-center justify-center animate-pulse
 "
        >
          <span class="i-mdi-loading w-8 h-8 animate-spin opacity-20" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style>
.filament-tiptap-grid {
  @apply grid grid-cols-1 gap-2 lg:grid-cols-2;
}

::-webkit-input-placeholder {
  opacity: 30%;
  font-weight: 700;
}

:-moz-placeholder {
  /* Firefox 18- */
  opacity: 30%;
  font-weight: 700;
}

::-moz-placeholder {
  /* Firefox 19+ */
  opacity: 30%;
  font-weight: 700;
}

:-ms-input-placeholder {
  opacity: 30%;
  font-weight: 700;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
